import {
  type Maybe,
  type TaskForScopedUsersQuery,
  type TaskQuery,
  type TaskWatcher,
  type User,
  UserScopeEnum,
} from "@src/__generated__/graphql";
import type { UserTooltipProps } from "@src/components/ui-kit";
import type { MakeOptional } from "@src/utils/types";
import { compact } from "lodash";
import { action, makeObservable, observable } from "mobx";

export type PositionBadgeUser = Maybe<
  Partial<UserTooltipProps["user"]> & {
    first_name?: string | null;
    last_name?: string | null;
    scope?: User["scope"];
  }
>;

type Positions = MakeOptional<
  TaskQuery["task"]["positions"][0],
  "planned_time" | "timeTrackingWorkType"
>[];
type OurWorkBudgetItem = MakeOptional<
  NonNullable<TaskQuery["task"]["ourWorkBudgetItem"]>,
  "title" | "timeTrackingSettings"
>;
type TTask = Omit<
  MakeOptional<
    TaskQuery["task"],
    | "billable"
    | "stats"
    | "commentCount"
    | "scopes"
    | "otherPeopleTracked"
    | "otherPeopleTrackedTotal"
    | "revision"
    | "taskWatchers"
    | "currentAssignee"
  >,
  "positions" | "ourWorkBudgetItem" | "parent"
> & {
  positions: Positions;
  ourWorkBudgetItem?: OurWorkBudgetItem | null;
  parent?:
    | TaskQuery["task"]["parent"]
    | TaskForScopedUsersQuery["task"]["parent"];
};
type TTaskPositionStat = TaskQuery["taskPositionStat"];

export type AssignedUser = Omit<Partial<TTaskPositionStat[0]>, "user"> & {
  user?: PositionBadgeUser;
};
export class TaskModel {
  id: TTask["id"];
  @observable.ref name: TTask["name"];
  @observable status: TTask["status"];
  @observable.ref deadline: TTask["deadline"];
  days_to_deadline?: TTask["days_to_deadline"];
  @observable.ref from?: TTask["from"];
  days_to_from?: TTask["days_to_from"];
  @observable.ref to?: TTask["to"];
  days_to_to?: TTask["days_to_to"];
  @observable.ref description: TTask["description"];
  @observable positions: TTask["positions"];
  @observable.ref priority: TTask["priority"];
  stats: TTask["stats"];
  ourWorkBudgetItem: TTask["ourWorkBudgetItem"];
  @observable files: TTask["files"];
  createdBy: TTask["createdBy"];
  created_at?: TTask["created_at"];
  attachments_download_url: TTask["attachments_download_url"];
  @observable partners: TTask["users"];
  @observable clients: TTask["users"];

  userIdsWithEditPermission: User["id"][];
  @observable assignedUsers: AssignedUser[] = [];

  otherPeopleTrackedTotal?: TTask["otherPeopleTrackedTotal"];
  @observable.ref billable?: TTask["billable"];
  @observable linkedTasks: TTask["linkedTasks"];
  revision?: TTask["revision"];
  otherPeopleTracked: TTask["otherPeopleTracked"];
  cloudUrl: TTask["cloudUrl"];
  driveCloudUrl: TTask["driveCloudUrl"];
  commentCount?: TTask["commentCount"];
  scopedCommentCount: TTask["scopedCommentCount"];
  clientCommentCount: TTask["clientCommentCount"];
  partnerCommentCount: TTask["partnerCommentCount"];
  parent?: TTask["parent"];
  subtasks?: TaskModel[];
  @observable is_template: TTask["is_template"];
  @observable.ref
  is_starred: TTask["is_starred"];
  @observable task_template_group_id?: TTask["task_template_group_id"];
  @observable.ref taskWatchersIds: TaskWatcher["user_id"][] = [];
  @observable.ref currentAssignee?: TTask["currentAssignee"] | null;

  constructor(task: TTask, positionsStats: TTaskPositionStat = []) {
    makeObservable(this);
    this.id = task.id;
    this.name = task.name;
    this.status = task.status;
    this.createdBy = task.createdBy;
    this.created_at = task.created_at;
    this.to = task.to;
    this.days_to_to = task.days_to_to;
    this.from = task.from;
    this.days_to_from = task.days_to_from;
    this.deadline = task.deadline;
    this.days_to_deadline = task.days_to_deadline;
    this.priority = task.priority;
    this.description = task.description;
    this.positions = task.positions;
    this.ourWorkBudgetItem = task.ourWorkBudgetItem;
    this.files = task.files;
    this.attachments_download_url = task.attachments_download_url;
    this.clients = task.users.filter(
      (i) => i.scope?.value === UserScopeEnum.Client,
    );
    this.partners = task.users.filter(
      (i) => i.scope?.value === UserScopeEnum.Partner,
    );
    this.userIdsWithEditPermission = compact([
      task.createdBy?.id,
      task.ourWorkBudgetItem?.project.projectManager.id,
      ...(task.ourWorkBudgetItem?.project.otherProjectManagers ?? []).map(
        ({ id }) => id,
      ),
    ]);

    for (const stat of positionsStats) {
      if (!stat) continue;
      this.assignedUsers.push(stat);
    }

    for (const partner of this.partners) {
      this.assignedUsers.push({
        user: partner,
        spent_time: undefined,
        planned_time: undefined,
        timeTrackingWorkType: undefined,
      });
    }

    for (const client of this.clients) {
      this.assignedUsers.push({
        user: client,
        spent_time: undefined,
        planned_time: undefined,
        timeTrackingWorkType: undefined,
      });
    }

    this.stats = task.stats;
    this.linkedTasks = task.linkedTasks;
    this.cloudUrl = task.cloudUrl;
    this.driveCloudUrl = task.driveCloudUrl;
    this.revision = task.revision;
    this.otherPeopleTracked = task.otherPeopleTracked;
    this.otherPeopleTrackedTotal = task.otherPeopleTrackedTotal;
    this.billable = task.billable;
    this.commentCount = task.commentCount;
    this.clientCommentCount = task.clientCommentCount;
    this.partnerCommentCount = task.partnerCommentCount;
    this.scopedCommentCount = task.scopedCommentCount;
    this.parent = task?.parent;
    this.subtasks =
      task.subtasks?.map((subtask) => new TaskModel(subtask)) ?? [];
    this.is_template = task.is_template;
    this.is_starred = task.is_starred;
    this.task_template_group_id = task?.task_template_group_id;
    this.currentAssignee = task.currentAssignee;
    this.setWatchers(task.taskWatchers);
  }

  @action.bound setCurrentAssignee(userId: User["id"] | null) {
    this.currentAssignee = userId
      ? ({ __typename: "User", id: userId } as TaskModel["currentAssignee"])
      : null;
  }

  get deadlineDate() {
    return this.deadline ? new Date(this.deadline) : undefined;
  }

  @action.bound setWatchers(watchers: TTask["taskWatchers"]) {
    this.taskWatchersIds = compact(
      watchers?.map((watcher) => watcher?.user_id),
    );
  }

  @action.bound setDescription(value: TaskModel["description"]) {
    this.description = value;
  }

  @action.bound setFiles(files: TaskModel["files"]) {
    this.files = files;
  }

  @action.bound removeFile(publicId: TaskModel["files"][0]["public_id"]) {
    this.files = this.files.filter((i) => i.public_id !== publicId);
  }

  @action.bound setStatus(value: TaskModel["status"]) {
    this.status = value;
  }

  @action.bound setName(value: TaskModel["name"]) {
    this.name = value;
  }

  @action.bound setPriority(value: TaskModel["priority"]) {
    this.priority = value;
  }

  @action.bound setBillable(value: TaskModel["billable"]) {
    this.billable = value;
  }

  @action.bound setSchedule({
    from,
    to,
    deadline,
  }: Pick<TaskModel, "from" | "to" | "deadline">) {
    this.from = from;
    this.to = to;
    this.deadline = deadline;
  }
}
